import { onlineManager, type QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, createRoute, createRouter, redirect } from '@tanstack/react-router';
import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import { accessScope, type Action, type Me, type Module } from '@repo/shared';
import { isApiError } from '../lib/api';
import { loadOffline, OFFLINE_ME_KEY } from '../lib/offline';
import { homePathFor, meQuery, useMe } from '../auth/session';
import { LoginPage } from '../pages/auth/LoginPage';
import { PlatformShell } from '../pages/platform/PlatformShell';
import { ComingSoonPage, ForbiddenPage, NotFoundPage } from '../pages/StatusPages';
import { AppShell } from '../shell/AppShell';
import { allNavItems, canSee } from '../shell/nav';
import { lazyPage, SuspenseOutlet } from './lazy';
import {
  type AbsencesSearch,
  AbsencesSearchSchema,
  type AuditSearch,
  AuditSearchSchema,
  type CandidaturesSearch,
  CandidaturesSearchSchema,
  type DocumentsSearch,
  DocumentsSearchSchema,
  type GradeReviewsSearch,
  GradeReviewsSearchSchema,
  type InternshipsSearch,
  InternshipsSearchSchema,
  type GradeSheetsSearch,
  GradeSheetsSearchSchema,
  type JurySearch,
  JurySearchSchema,
  type SchedulesSearch,
  SchedulesSearchSchema,
  type StudentSearch,
  StudentSearchSchema,
  type StudentsSearch,
  StudentsSearchSchema,
  type TimetableSearch,
  TimetableSearchSchema,
  type UnpaidSearch,
  UnpaidSearchSchema,
  settingsSearch,
} from './search';

// Every page is its own chunk (Phase 7): the first load only fetches the shell and the current page.
/** Component library: development builds only (Vite drops the chunk from production bundles). */
const DesignLibraryPage = import.meta.env.DEV
  ? lazyPage(() => import('../pages/design/DesignLibraryPage'), 'DesignLibraryPage')
  : null;
const ResetPasswordPage = lazyPage(() => import('../pages/auth/ResetPasswordPage'), 'ResetPasswordPage');
const PlatformTenantsPage = lazyPage(() => import('../pages/platform/PlatformPages'), 'PlatformTenantsPage');
const PlatformJournalPage = lazyPage(
  () => import('../pages/platform/PlatformJournalPage'),
  'PlatformJournalPage',
);
const DashboardPage = lazyPage(() => import('../pages/dashboard/DashboardPage'), 'DashboardPage');
const StudentApp = lazyPage(() => import('../pages/student/StudentApp'), 'StudentApp');
const ApplyPage = lazyPage(() => import('../pages/admissions/ApplyPage'), 'ApplyPage');
const CandidaturesPage = lazyPage(() => import('../pages/admissions/CandidaturesPage'), 'CandidaturesPage');
const PreinscriptionsPage = lazyPage(
  () => import('../pages/admissions/PreinscriptionsPage'),
  'PreinscriptionsPage',
);
const CandidatePage = lazyPage(() => import('../pages/admissions/TrackPage'), 'CandidatePage');
const TrackPage = lazyPage(() => import('../pages/admissions/TrackPage'), 'TrackPage');
const CurriculaPage = lazyPage(() => import('../pages/curriculum/CurriculaPage'), 'CurriculaPage');
const StudyPlanPage = lazyPage(() => import('../pages/curriculum/StudyPlanPage'), 'StudyPlanPage');
const JuriesPage = lazyPage(() => import('../pages/deliberations/JuriesPage'), 'JuriesPage');
const JuryPage = lazyPage(() => import('../pages/deliberations/JuryPage'), 'JuryPage');
const DocumentsPage = lazyPage(() => import('../pages/documents/DocumentsPage'), 'DocumentsPage');
const TranscriptPage = lazyPage(() => import('../pages/documents/TranscriptPage'), 'TranscriptPage');
const VerifyPage = lazyPage(() => import('../pages/verify/VerifyPage'), 'VerifyPage');
const GradeEntryPage = lazyPage(() => import('../pages/grades/GradeEntryPage'), 'GradeEntryPage');
const GradeSheetsPage = lazyPage(() => import('../pages/grades/GradeSheetsPage'), 'GradeSheetsPage');
const GradeReviewsPage = lazyPage(() => import('../pages/grades/GradeReviewsPage'), 'GradeReviewsPage');
const CurriculumBuilderPage = lazyPage(
  () => import('../pages/curriculum/CurriculumBuilderPage'),
  'CurriculumBuilderPage',
);
const NewStudentPage = lazyPage(() => import('../pages/students/NewStudentPage'), 'NewStudentPage');
const StudentRecordPage = lazyPage(() => import('../pages/students/StudentRecordPage'), 'StudentRecordPage');
const StudentsPage = lazyPage(() => import('../pages/students/StudentsPage'), 'StudentsPage');
const TeachersPage = lazyPage(() => import('../pages/teachers/TeachersPage'), 'TeachersPage');
const AbsencesPage = lazyPage(() => import('../pages/attendance/AbsencesPage'), 'AbsencesPage');
const RollCallHome = lazyPage(() => import('../pages/attendance/RollCallHome'), 'RollCallHome');
const RollCallPage = lazyPage(() => import('../pages/attendance/RollCallPage'), 'RollCallPage');
const AnnouncementsPage = lazyPage(
  () => import('../pages/announcements/AnnouncementsPage'),
  'AnnouncementsPage',
);
const InternshipsPage = lazyPage(() => import('../pages/internships/InternshipsPage'), 'InternshipsPage');
const InternshipPage = lazyPage(() => import('../pages/internships/InternshipPage'), 'InternshipPage');
const SessionPage = lazyPage(() => import('../pages/internships/SessionPage'), 'SessionPage');
const CalendarPage = lazyPage(() => import('../pages/calendar/CalendarPage'), 'CalendarPage');
const TimetablePage = lazyPage(() => import('../pages/timetable/TimetablePage'), 'TimetablePage');
const SchedulesPage = lazyPage(() => import('../pages/finance/SchedulesPage'), 'SchedulesPage');
const UnpaidPage = lazyPage(() => import('../pages/finance/UnpaidPage'), 'UnpaidPage');
const SettingsPage = lazyPage(() => import('../pages/settings/SettingsPage'), 'SettingsPage');
const AuditPage = lazyPage(() => import('../pages/audit/AuditPage'), 'AuditPage');

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: SuspenseOutlet,
  notFoundComponent: NotFoundPage,
});

/** Offline, a student keeps their last session to read cached data (Phase 6 PWA). */
function offlineUser(qc: QueryClient): Me | null {
  const saved = loadOffline<Me>(OFFLINE_ME_KEY)?.data ?? null;
  if (saved) qc.setQueryData(meQuery.queryKey, saved);
  return saved;
}

async function currentUser(qc: QueryClient): Promise<Me | null> {
  // An offline fetch would stay paused until the network is back.
  if (!onlineManager.isOnline() && !qc.getQueryData(meQuery.queryKey)) return offlineUser(qc);
  try {
    return await qc.query({ ...meQuery, staleTime: 'static' });
  } catch (e) {
    return isApiError(e) ? null : offlineUser(qc);
  }
}

// ── Public ──────────────────────────────────────────────────────────────────
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>): { redirect?: string } =>
    typeof s.redirect === 'string' ? { redirect: s.redirect } : {},
  beforeLoad: async ({ context }) => {
    const me = await currentUser(context.queryClient);
    if (me) throw redirect({ to: homePathFor(me) });
  },
  component: LoginPage,
});
const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: (s: Record<string, unknown>): { token?: string } =>
    typeof s.token === 'string' ? { token: s.token } : {},
  component: ResetPasswordPage,
});
/** Public document verification (QR codes land on /verify/<code>). */
const verifyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/verify', component: VerifyPage });
const verifyCodeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify/$code',
  component: VerifyPage,
});
const applyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/preinscription',
  component: ApplyPage,
});
const trackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/preinscription/suivi',
  validateSearch: (s: Record<string, unknown>): { t?: string } => (typeof s.t === 'string' ? { t: s.t } : {}),
  component: TrackPage,
});

// ── Authenticated ───────────────────────────────────────────────────────────
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  beforeLoad: async ({ context, location }) => {
    const me = await currentUser(context.queryClient);
    if (!me) throw redirect({ to: '/login', search: { redirect: location.href } });
    return { me };
  },
  component: SuspenseOutlet,
});

const isBackOffice = (me: Me) => !me.roles.every((r) => r === 'ETUDIANT' || r === 'CANDIDAT');

/** Full-page refusal outside any shell (a student or candidate opening a back-office or another role's URL). */
function StandaloneForbidden() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default' }}>
      <Box sx={{ m: 'auto' }}>
        <ForbiddenPage />
      </Box>
    </Box>
  );
}

const appRoute = createRoute({
  getParentRoute: () => authRoute,
  id: 'app',
  beforeLoad: ({ context, location }) => {
    const me = context.queryClient.getQueryData(meQuery.queryKey);
    // The root goes to the user's own space; any other back-office URL says it is not theirs.
    if (me && !isBackOffice(me) && location.pathname === '/') throw redirect({ to: homePathFor(me) });
  },
  component: function AppOrForbidden() {
    return isBackOffice(useMe()) ? <AppShell /> : <StandaloneForbidden />;
  },
});

function Dashboard() {
  const me = useMe();
  return me.permissions.dashboard === 'none' ? <ForbiddenPage /> : <DashboardPage />;
}
const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    const me = context.queryClient.getQueryData(meQuery.queryKey);
    if (me && me.permissions.dashboard === 'none') throw redirect({ to: homePathFor(me) });
  },
  component: Dashboard,
});

/** Modules shipped in later phases: guarded like the real pages, rendered as placeholders for now. */
function Guarded({ navKey }: { navKey: string }) {
  const me = useMe();
  const item = allNavItems.find((i) => i.key === navKey);
  return item && canSee(me, item) ? <ComingSoonPage navKey={navKey} /> : <ForbiddenPage />;
}
/** Guard for implemented pages: same permission check as the navigation (the API enforces it too). */
function RequirePermission({
  module,
  action = 'read',
  allowOwn = false,
  children,
}: {
  module: Module;
  action?: Action;
  allowOwn?: boolean;
  children: ReactNode;
}) {
  const me = useMe();
  const scope = accessScope(me.permissions, module, action);
  return scope === 'all' || (scope === 'own' && allowOwn) ? <>{children}</> : <ForbiddenPage />;
}

const studentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/etudiants',
  validateSearch: (s: Record<string, unknown>): StudentsSearch => StudentsSearchSchema.parse(s),
  component: () => (
    <RequirePermission module="students" allowOwn>
      <StudentsPage />
    </RequirePermission>
  ),
});
const teachersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/enseignants',
  component: () => (
    <RequirePermission module="students" action="edit">
      <TeachersPage />
    </RequirePermission>
  ),
});
const newStudentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/etudiants/nouveau',
  component: () => (
    <RequirePermission module="students" action="edit">
      <NewStudentPage />
    </RequirePermission>
  ),
});
const studentFileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/etudiants/$studentId',
  validateSearch: (s: Record<string, unknown>): { tab?: string } =>
    typeof s.tab === 'string' ? { tab: s.tab } : {},
  component: () => (
    <RequirePermission module="students" allowOwn>
      <StudentRecordPage />
    </RequirePermission>
  ),
});
const curriculaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/formations',
  component: () => (
    <RequirePermission module="curriculum" allowOwn>
      <CurriculaPage />
    </RequirePermission>
  ),
});
const studyPlanRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/formations/plan',
  component: () => (
    <RequirePermission module="curriculum" allowOwn>
      <StudyPlanPage />
    </RequirePermission>
  ),
});
const curriculumRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/formations/$curriculumId',
  component: () => (
    <RequirePermission module="curriculum" allowOwn>
      <CurriculumBuilderPage />
    </RequirePermission>
  ),
});

const gradeSheetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/notes',
  validateSearch: (s: Record<string, unknown>): GradeSheetsSearch => GradeSheetsSearchSchema.parse(s),
  component: () => (
    <RequirePermission module="grades" allowOwn>
      <GradeSheetsPage />
    </RequirePermission>
  ),
});
const gradeReviewsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/notes/verifications',
  validateSearch: (s: Record<string, unknown>): GradeReviewsSearch => GradeReviewsSearchSchema.parse(s),
  component: () => (
    <RequirePermission module="grades" allowOwn>
      <GradeReviewsPage />
    </RequirePermission>
  ),
});
const gradeEntryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/notes/$sheetId',
  component: () => (
    <RequirePermission module="grades" allowOwn>
      <GradeEntryPage />
    </RequirePermission>
  ),
});
const juriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/deliberations',
  component: () => (
    <RequirePermission module="deliberations">
      <JuriesPage />
    </RequirePermission>
  ),
});
const juryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/deliberations/$juryId',
  validateSearch: (s: Record<string, unknown>): JurySearch => JurySearchSchema.parse(s),
  component: () => (
    <RequirePermission module="deliberations">
      <JuryPage />
    </RequirePermission>
  ),
});

const documentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/documents',
  validateSearch: (s: Record<string, unknown>): DocumentsSearch => DocumentsSearchSchema.parse(s),
  component: () => (
    <RequirePermission module="documents">
      <DocumentsPage />
    </RequirePermission>
  ),
});
const transcriptRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/etudiants/$studentId/releve',
  component: () => (
    <RequirePermission module="documents" action="edit">
      <TranscriptPage />
    </RequirePermission>
  ),
});

const candidaturesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/candidatures',
  validateSearch: (s: Record<string, unknown>): CandidaturesSearch => CandidaturesSearchSchema.parse(s),
  component: () => (
    <RequirePermission module="admissions">
      <CandidaturesPage />
    </RequirePermission>
  ),
});
const preinscriptionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/preinscriptions',
  component: () => (
    <RequirePermission module="admissions">
      <PreinscriptionsPage />
    </RequirePermission>
  ),
});

const absencesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/absences',
  validateSearch: (s: Record<string, unknown>): AbsencesSearch => AbsencesSearchSchema.parse(s),
  component: () => (
    <RequirePermission module="attendance" allowOwn>
      <AbsencesPage />
    </RequirePermission>
  ),
});
const rollCallHomeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/appel',
  component: () => (
    <RequirePermission module="attendance" action="edit" allowOwn>
      <RollCallHome />
    </RequirePermission>
  ),
});
/** Full-screen on the teacher's phone: outside the back-office shell. */
const rollCallRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/appel/$sessionId',
  component: () => (
    <RequirePermission module="attendance" action="edit" allowOwn>
      <RollCallPage />
    </RequirePermission>
  ),
});
const announcementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/annonces',
  component: () => (
    <RequirePermission module="announcements">
      <AnnouncementsPage />
    </RequirePermission>
  ),
});
const internshipsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/stages',
  validateSearch: (s: Record<string, unknown>): InternshipsSearch => InternshipsSearchSchema.parse(s),
  component: () => (
    <RequirePermission module="internships" allowOwn>
      <InternshipsPage />
    </RequirePermission>
  ),
});
const internshipRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/stages/$internshipId',
  component: () => (
    <RequirePermission module="internships" allowOwn>
      <InternshipPage />
    </RequirePermission>
  ),
});
const defenseSessionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/stages/soutenances/$sessionId',
  component: () => (
    <RequirePermission module="internships">
      <SessionPage />
    </RequirePermission>
  ),
});
const calendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/calendrier',
  component: () => (
    <RequirePermission module="announcements">
      <CalendarPage />
    </RequirePermission>
  ),
});
const timetableRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/emploi-du-temps',
  validateSearch: (s: Record<string, unknown>): TimetableSearch => TimetableSearchSchema.parse(s),
  component: () => (
    <RequirePermission module="timetable" allowOwn>
      <TimetablePage />
    </RequirePermission>
  ),
});
const schedulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/finance/echeanciers',
  validateSearch: (s: Record<string, unknown>): SchedulesSearch => SchedulesSearchSchema.parse(s),
  component: () => (
    <RequirePermission module="finance">
      <SchedulesPage />
    </RequirePermission>
  ),
});
const unpaidRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/finance/impayes',
  validateSearch: (s: Record<string, unknown>): UnpaidSearch => UnpaidSearchSchema.parse(s),
  component: () => (
    <RequirePermission module="finance">
      <UnpaidPage />
    </RequirePermission>
  ),
});
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/parametres',
  validateSearch: settingsSearch,
  component: () => (
    <RequirePermission module="settings" action="edit">
      <SettingsPage />
    </RequirePermission>
  ),
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/journal',
  validateSearch: (s: Record<string, unknown>): AuditSearch => AuditSearchSchema.parse(s),
  component: () => (
    <RequirePermission module="audit">
      <AuditPage />
    </RequirePermission>
  ),
});

const IMPLEMENTED = new Set([
  '/',
  '/etudiants',
  '/enseignants',
  '/formations',
  '/notes',
  '/deliberations',
  '/documents',
  '/candidatures',
  '/preinscriptions',
  '/absences',
  '/emploi-du-temps',
  '/annonces',
  '/appel',
  '/finance/echeanciers',
  '/finance/impayes',
  '/calendrier',
  '/stages',
  '/parametres',
  '/journal',
]);
const moduleRoutes = allNavItems
  .filter((i) => !IMPLEMENTED.has(i.to))
  .map((i) =>
    createRoute({ getParentRoute: () => appRoute, path: i.to, component: () => <Guarded navKey={i.key} /> }),
  );
const designRoutes = DesignLibraryPage
  ? [createRoute({ getParentRoute: () => appRoute, path: '/design', component: DesignLibraryPage })]
  : [];

const studentHomeRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/etudiant',
  validateSearch: (s: Record<string, unknown>): StudentSearch => StudentSearchSchema.parse(s),
  component: function StudentHome() {
    return useMe().roles.includes('ETUDIANT') ? <StudentApp /> : <StandaloneForbidden />;
  },
});
const candidateHomeRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/candidature',
  component: function CandidateHome() {
    return useMe().roles.includes('CANDIDAT') ? <CandidatePage /> : <StandaloneForbidden />;
  },
});

// ── Platform console (admin.<domain>) ───────────────────────────────────────
const platformShellRoute = createRoute({
  getParentRoute: () => authRoute,
  id: 'platform-shell',
  component: PlatformShell,
});
const platformIndexRoute = createRoute({
  getParentRoute: () => platformShellRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/platform' });
  },
});
const platformTenantsRoute = createRoute({
  getParentRoute: () => platformShellRoute,
  path: '/platform',
  component: PlatformTenantsPage,
});
const platformJournalRoute = createRoute({
  getParentRoute: () => platformShellRoute,
  path: '/platform/journal',
  component: PlatformJournalPage,
});

export function createAppRouter(kind: 'tenant' | 'platform', queryClient: QueryClient) {
  const tree =
    kind === 'platform'
      ? rootRoute.addChildren([
          loginRoute,
          resetRoute,
          authRoute.addChildren([
            platformShellRoute.addChildren([platformIndexRoute, platformTenantsRoute, platformJournalRoute]),
          ]),
        ])
      : rootRoute.addChildren([
          loginRoute,
          resetRoute,
          applyRoute,
          trackRoute,
          verifyRoute,
          verifyCodeRoute,
          authRoute.addChildren([
            appRoute.addChildren([
              indexRoute,
              studentsRoute,
              teachersRoute,
              newStudentRoute,
              studentFileRoute,
              curriculaRoute,
              studyPlanRoute,
              curriculumRoute,
              gradeSheetsRoute,
              gradeReviewsRoute,
              gradeEntryRoute,
              juriesRoute,
              juryRoute,
              documentsRoute,
              transcriptRoute,
              candidaturesRoute,
              preinscriptionsRoute,
              absencesRoute,
              timetableRoute,
              announcementsRoute,
              calendarRoute,
              internshipsRoute,
              internshipRoute,
              defenseSessionRoute,
              rollCallHomeRoute,
              schedulesRoute,
              unpaidRoute,
              settingsRoute,
              auditRoute,
              ...moduleRoutes,
              ...designRoutes,
            ]),
            studentHomeRoute,
            candidateHomeRoute,
            rollCallRoute,
          ]),
        ]);
  return createRouter({
    routeTree: tree,
    context: { queryClient },
    defaultPreload: 'intent',
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
